import { Alert, Box, Button, CircularProgress, Typography } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { errorMessageOf } from "v2/utils/errorMessageOf";

import { MonitorActionDialog } from "./MonitorActionDialog";
import { MonitorAction, MonitorQueue } from "./MonitorQueue";
import { MonitorTop } from "./MonitorTop";
import { fetchMonitor, MONITOR_QUERY_PREFIX, monitorQueryKey } from "./monitor.service";
import { MonitorableType, MonitorFilter, MonitorOutcomes, MonitorSource } from "./monitor.types";
import { applyFilters, FILTER_LABELS, OUTCOME_LABELS, parseFilterParam, rowKey } from "./monitor.utils";

export interface CampaignMonitorProps {
  source: MonitorSource;
  type: MonitorableType;
  /** why: sem este valor, vale o `?filtro=` da URL (o link do e-mail do RH usa `filtro=pendentes`). */
  initialFilters?: MonitorFilter[];
}

export function CampaignMonitor({ source, type, initialFilters }: CampaignMonitorProps) {
  const { search } = useLocation();
  const [filters, setFilters] = useState<MonitorFilter[]>(() => initialFilters ?? parseFilterParam(search));
  const [expanded, setExpanded] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [action, setAction] = useState<MonitorAction | null>(null);
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: monitorQueryKey(source, type), queryFn: () => fetchMonitor(source, type), staleTime: 60_000 });

  if (query.isPending) return <CircularProgress size={24} aria-label="Carregando o monitor" />;
  if (query.isError) {
    return (
      <Alert severity="error" action={<Button onClick={() => query.refetch()}>Tentar novamente</Button>}>
        {errorMessageOf(query.error, "Não foi possível carregar o monitor.")}
      </Alert>
    );
  }

  const data = query.data;
  const rows = applyFilters(data.queue, filters);
  /** invariant: ação em lote só sobre linhas na tela; selecionada e escondida pelo filtro fica de fora. */
  const selectedRows = rows.filter((r) => selected.includes(rowKey(r)));
  const unread = data.contracts.filter((c) => c.unreadable).length;
  // why: o backend só preenche `viewer.sellerOrganizationId` quando a sessão é a vendedora (R13), nas visões
  // por contrato e por CNPJ; a carteira de outra vendedora nunca vira link, e o backend a recusa com 404.
  const seller = data.viewer.sellerOrganizationId;
  const toggleFilter = (f: MonitorFilter) => setFilters((cur) => (cur.includes(f) ? cur.filter((x) => x !== f) : [...cur, f]));
  const toggleRow = (key: string) => setSelected((cur) => (cur.includes(key) ? cur.filter((x) => x !== key) : [...cur, key]));

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {seller && (
        <Box>
          <Button component={Link} to={`/v2/sellers/${seller}/campanhas/${type}`} variant="outlined" size="small">
            Ver toda a carteira
          </Button>
        </Box>
      )}
      <MonitorTop data={data} source={source} />
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        {(Object.keys(FILTER_LABELS) as MonitorFilter[]).map((f) => (
          <Button
            key={f}
            size="small"
            variant={filters.includes(f) ? "contained" : "outlined"}
            aria-pressed={filters.includes(f)}
            onClick={() => toggleFilter(f)}
          >
            {FILTER_LABELS[f]}
          </Button>
        ))}
      </Box>
      {selectedRows.length > 0 && (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          <Typography variant="body2">{`${selectedRows.length} selecionado(s)`}</Typography>
          <Button size="small" variant="outlined" onClick={() => setAction({ kind: "RESEND", rows: selectedRows })}>Reenviar agora em lote</Button>
          <Button size="small" variant="outlined" onClick={() => setAction({ kind: "DECIDE", rows: selectedRows })}>Decidir pelo RH em lote</Button>
          <Button size="small" variant="outlined" onClick={() => setAction({ kind: "CLOSE", rows: selectedRows })}>Encerrar em lote</Button>
        </Box>
      )}
      <MonitorQueue
        rows={rows}
        contracts={data.contracts}
        showContract={data.scope.kind === "ORGANIZATION"}
        expanded={expanded}
        selected={selected}
        onExpand={setExpanded}
        onToggle={toggleRow}
        onAction={setAction}
      />
      {/* invariant: desfechos também excluem os contratos não lidos; com todos não lidos, zero seria mentira. */}
      {unread < data.contracts.length && (
        <Box>
          <Typography variant="subtitle2">{unread ? "Desfechos nos contratos lidos" : "Desfechos"}</Typography>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            {(Object.keys(OUTCOME_LABELS) as (keyof MonitorOutcomes)[]).map((k) => (
              <Typography key={k} variant="body2">{`${OUTCOME_LABELS[k]}: ${data.outcomes[k]}`}</Typography>
            ))}
          </Box>
        </Box>
      )}
      {action && (
        <MonitorActionDialog
          action={action}
          data={data}
          type={type}
          onClose={() => setAction(null)}
          onDone={() => {
            setAction(null);
            setSelected([]);
            queryClient.invalidateQueries({ queryKey: MONITOR_QUERY_PREFIX });
          }}
        />
      )}
    </Box>
  );
}
